import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  ButtonGroup,
  Divider,
  Alert,
  TextField,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  CircularProgress,
  Tooltip,
  IconButton,
  InputAdornment
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import Collapse from '@mui/material/Collapse';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CommentIcon from '@mui/icons-material/Comment';
import PendingActionsIcon from '@mui/icons-material/PendingActions';

// Import new AI mapping components
import OmopCriticAgentPanel from './OmopCriticAgentPanel';
import OmopMappingActions from './OmopMappingActions';
import OmopVocabularySearch from './OmopVocabularySearch';
import OmopSuggestionsTable from './OmopSuggestionsTable';

// Import mock service
import mockMappingService from '../../services/mockMappingService';
// Import vocabulary data
import omopVocabulary from '../../data/omopVocabulary.json';

/**
 * OmopMappingDetail Component
 * Comprehensive OMOP mapping section with AI Multi-Agent System integration
 * Shows detailed OMOP information, AI agent outputs, critic agent, and vocabulary browser
 *
 * @param {object} props
 * @param {object} props.parameter - Full parameter object with omopMapping data
 * @param {function} props.onMappingUpdate - Callback when mapping is updated
 * @param {string} props.userRole - Current user's role (user, data_steward, admin)
 */
const OmopMappingDetail = ({
  parameter,
  onMappingUpdate,
  userRole = 'user'
}) => {
  // Debug: Log the current user role
  console.log('OmopMappingDetail - Current userRole:', userRole);

  // Extract mapping data from parameter
  const omopMapping = parameter?.omopMapping || {};
  const omopStatus = omopMapping.status || 'unmapped';
  const topMatch = omopMapping.topMatch;
  const aiMetadata = omopMapping.aiMetadata;
  const agent3Output = aiMetadata?.agent3Output;
  const alternatives = omopMapping.alternatives || [];
  const validationHistory = omopMapping.validationHistory || [];
  const criticHistory = omopMapping.criticHistory || [];

  // Community & Collaboration data
  const communityLikes = omopMapping.communityLikes || 0;
  const communityDislikes = omopMapping.communityDislikes || 0;
  const proposedMappings = omopMapping.proposedMappings || [];
  console.log('OmopMappingDetail - proposedMappings count:', proposedMappings.length);
  console.log('OmopMappingDetail - Show approve/reject buttons?', userRole === 'data_steward' || userRole === 'admin');
  const comments = omopMapping.comments || [];
  const mappingHistory = omopMapping.mappingHistory || [];

  // State management
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCriticDialog, setShowCriticDialog] = useState(false);
  const [showVocabSearchDialog, setShowVocabSearchDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentStage, setCurrentStage] = useState(null);
  const [feedback, setFeedback] = useState('');

  // Pin management - track user-pinned concepts
  const [pinnedConceptIds, setPinnedConceptIds] = useState(
    omopMapping.userPinnedConcepts?.map(p => p.conceptId) || []
  );
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [showFeedbackSection, setShowFeedbackSection] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showChangeRequestDialog, setShowChangeRequestDialog] = useState(false);
  const [showCommentsDialog, setShowCommentsDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);

  // Change request form state
  const [changeRequestReason, setChangeRequestReason] = useState('');
  const [changeRequestJustification, setChangeRequestJustification] = useState('');
  const [selectedAlternativeConcept, setSelectedAlternativeConcept] = useState(null);
  const [showVocabSearchInDialog, setShowVocabSearchInDialog] = useState(false);

  // Proposal interaction state
  const [showProposalCommentInput, setShowProposalCommentInput] = useState({});
  const [proposalCommentText, setProposalCommentText] = useState({});

  // Mapping interaction state (for current + history)
  const [showMappingCommentInput, setShowMappingCommentInput] = useState({});
  const [mappingCommentText, setMappingCommentText] = useState({});

  // History collapse state
  const [showMappingHistory, setShowMappingHistory] = useState(false);

  // Info dialogs
  const [showCommunityInfoDialog, setShowCommunityInfoDialog] = useState(false);
  const [showOmopInfoDialog, setShowOmopInfoDialog] = useState(false);

  // Manual proposal form state
  const [showManualProposalForm, setShowManualProposalForm] = useState(false);
  const [manualProposalData, setManualProposalData] = useState({
    conceptId: '',
    conceptName: '',
    vocabulary: '',
    domain: '',
    justification: ''
  });

  // Calculate mapping quality from AI metadata or validation history
  const mappingQuality = {
    score: aiMetadata?.confidenceScore
      ? Math.round(aiMetadata.confidenceScore * 100)
      : (omopStatus === 'validated' ? 95 : omopStatus === 'mapped' ? 75 : 0),
    confidence: aiMetadata?.confidenceScore >= 0.9 ? 'Sehr hoch'
      : aiMetadata?.confidenceScore >= 0.7 ? 'Hoch'
      : aiMetadata?.confidenceScore >= 0.5 ? 'Mittel'
      : 'Niedrig',
    validatedBy: validationHistory[0]?.user || null,
    validatedDate: validationHistory[0]?.timestamp || null,
    mappedBy: aiMetadata ? 'AI Multi-Agent System' : 'Manuell',
    mappedDate: omopMapping.mappedDate || new Date().toISOString()
  };

  // Action Handlers
  const handleGenerateMapping = async () => {
    setIsProcessing(true);
    setCurrentStage(null);

    try {
      const result = await mockMappingService.generateAiMapping(
        parameter,
        (stageName, status, data) => {
          setCurrentStage({ name: stageName, status, ...data });
        }
      );

      if (result.status === 'success') {
        onMappingUpdate && onMappingUpdate({
          ...parameter,
          omopStatus: 'mapped',
          omopMapping: result.omopMapping
        });
        setSnackbar({
          open: true,
          message: 'AI Mapping erfolgreich generiert!',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Fehler beim Generieren des Mappings',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Ein Fehler ist aufgetreten',
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
      setCurrentStage(null);
    }
  };

  const handleValidate = async () => {
    setIsProcessing(true);

    try {
      const result = await mockMappingService.validateMapping(parameter.id);

      if (result.status === 'success') {
        const updatedMapping = {
          ...omopMapping,
          status: 'validated',
          validationHistory: [
            {
              action: 'validated',
              user: result.validatedBy,
              timestamp: result.timestamp,
              conceptId: topMatch.conceptId,
              conceptName: topMatch.conceptName
            },
            ...validationHistory
          ]
        };

        onMappingUpdate && onMappingUpdate({
          ...parameter,
          omopStatus: 'validated',
          omopMapping: updatedMapping
        });

        setSnackbar({
          open: true,
          message: 'Mapping erfolgreich validiert!',
          severity: 'success'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Fehler beim Validieren',
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async () => {
    setIsProcessing(true);

    try {
      const result = await mockMappingService.rejectMapping(parameter.id);

      if (result.status === 'success') {
        const updatedMapping = {
          ...omopMapping,
          status: 'unmapped',
          topMatch: null,
          aiMetadata: null
        };

        onMappingUpdate && onMappingUpdate({
          ...parameter,
          omopStatus: 'unmapped',
          omopMapping: updatedMapping
        });

        setSnackbar({
          open: true,
          message: 'Mapping abgelehnt',
          severity: 'info'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Fehler beim Ablehnen',
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCriticFeedback = async (feedback) => {
    setIsProcessing(true);

    try {
      const result = await mockMappingService.submitCriticFeedback(
        parameter.id,
        feedback,
        omopMapping
      );

      if (result.status === 'success') {
        onMappingUpdate && onMappingUpdate({
          ...parameter,
          omopMapping: result.omopMapping
        });

        setSnackbar({
          open: true,
          message: 'Critic Agent Feedback verarbeitet!',
          severity: 'success'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Fehler beim Verarbeiten des Feedbacks',
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSelectAlternative = async (candidate) => {
    setIsProcessing(true);

    try {
      // Update mapping with selected alternative as new top match
      const updatedMapping = {
        ...omopMapping,
        status: 'mapped',
        topMatch: {
          conceptId: candidate.conceptId,
          conceptCode: candidate.conceptCode || candidate.conceptId,
          conceptName: candidate.conceptName,
          vocabulary: candidate.vocabulary,
          domain: candidate.domain || 'Measurement'
        },
        aiMetadata: {
          ...aiMetadata,
          confidenceScore: candidate.score || 0.85,
          // Keep agent outputs but update top candidate
          agent3Output: {
            ...agent3Output,
            allCandidates: agent3Output.allCandidates
          }
        }
      };

      onMappingUpdate && onMappingUpdate({
        ...parameter,
        omopStatus: 'mapped',
        omopMapping: updatedMapping
      });

      setSnackbar({
        open: true,
        message: `Alternative "${candidate.conceptName}" ausgewählt!`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Fehler beim Auswählen',
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTogglePin = async (candidate) => {
    const conceptId = candidate.conceptId;
    const isPinned = pinnedConceptIds.includes(conceptId);

    try {
      let updatedPinnedIds;
      let updatedPinnedConcepts;

      if (isPinned) {
        // Unpin
        updatedPinnedIds = pinnedConceptIds.filter(id => id !== conceptId);
        updatedPinnedConcepts = (omopMapping.userPinnedConcepts || []).filter(
          p => p.conceptId !== conceptId
        );
      } else {
        // Pin
        updatedPinnedIds = [...pinnedConceptIds, conceptId];
        updatedPinnedConcepts = [
          ...(omopMapping.userPinnedConcepts || []),
          {
            conceptId: candidate.conceptId,
            conceptCode: candidate.conceptCode,
            conceptName: candidate.conceptName,
            vocabulary: candidate.vocabulary,
            domain: candidate.domain,
            score: candidate.score,
            pinnedAt: new Date().toISOString(),
            pinnedBy: 'Current User' // TODO: Get from auth context
          }
        ];
      }

      setPinnedConceptIds(updatedPinnedIds);

      // Update parent component
      const updatedMapping = {
        ...omopMapping,
        userPinnedConcepts: updatedPinnedConcepts
      };

      onMappingUpdate && onMappingUpdate({
        ...parameter,
        omopMapping: updatedMapping
      });

      setSnackbar({
        open: true,
        message: isPinned
          ? `"${candidate.conceptName}" nicht mehr gespeichert`
          : `"${candidate.conceptName}" gespeichert`,
        severity: 'info'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Fehler beim Speichern',
        severity: 'error'
      });
    }
  };

  const handleManualMapping = async (concept) => {
    setIsProcessing(true);

    try {
      const result = await mockMappingService.saveManualMapping(parameter.id, concept);

      if (result.status === 'success') {
        const updatedMapping = {
          status: 'mapped',
          topMatch: {
            conceptId: concept.conceptId,
            conceptCode: concept.conceptCode,
            conceptName: concept.conceptName,
            vocabulary: concept.vocabulary,
            domain: concept.domain
          },
          alternatives: [],
          aiMetadata: null,
          validationHistory: [],
          criticHistory: []
        };

        onMappingUpdate && onMappingUpdate({
          ...parameter,
          omopStatus: 'mapped',
          omopMapping: updatedMapping
        });

        setShowVocabSearchDialog(false);
        setSnackbar({
          open: true,
          message: 'Manuelles Mapping gespeichert!',
          severity: 'success'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Fehler beim Speichern',
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmitChangeRequest = () => {
    // Validate form
    if (!changeRequestReason) {
      setSnackbar({
        open: true,
        message: 'Bitte wählen Sie einen Grund für die Änderung',
        severity: 'warning'
      });
      return;
    }

    if (!changeRequestJustification.trim()) {
      setSnackbar({
        open: true,
        message: 'Bitte geben Sie eine Begründung an',
        severity: 'warning'
      });
      return;
    }

    if (!selectedAlternativeConcept) {
      setSnackbar({
        open: true,
        message: 'Bitte wählen Sie ein alternatives Concept',
        severity: 'warning'
      });
      return;
    }

    // Create new proposal
    const newProposal = {
      id: `prop_${Date.now()}`,
      concept: selectedAlternativeConcept,
      proposedBy: 'Current User', // Would come from auth context in real app
      proposedAt: new Date().toISOString(),
      reason: changeRequestReason,
      justification: changeRequestJustification,
      votes: 0,
      status: 'pending_review'
    };

    // Add to proposed mappings
    const updatedMapping = {
      ...omopMapping,
      proposedMappings: [...proposedMappings, newProposal]
    };

    onMappingUpdate && onMappingUpdate({
      ...parameter,
      omopMapping: updatedMapping
    });

    // Reset form and close dialog
    setChangeRequestReason('');
    setChangeRequestJustification('');
    setSelectedAlternativeConcept(null);
    setShowVocabSearchInDialog(false);
    setShowChangeRequestDialog(false);

    setSnackbar({
      open: true,
      message: 'Änderungsvorschlag erfolgreich eingereicht!',
      severity: 'success'
    });
  };

  // Handle proposal submission from table
  const handleSubmitProposalFromTable = ({ candidate, justification }) => {
    const newProposal = {
      id: `prop_${Date.now()}`,
      concept: {
        conceptId: candidate.conceptId,
        conceptCode: candidate.conceptCode || candidate.conceptId,
        conceptName: candidate.conceptName,
        vocabulary: candidate.vocabulary,
        domain: candidate.domain || 'Measurement'
      },
      proposedBy: 'Current User',
      proposedAt: new Date().toISOString(),
      justification: justification || '',
      votes: 0,
      likes: 0,
      dislikes: 0,
      likedBy: [],
      dislikedBy: [],
      comments: [],
      status: 'pending_review'
    };

    const updatedMapping = {
      ...omopMapping,
      proposedMappings: [...proposedMappings, newProposal]
    };

    onMappingUpdate && onMappingUpdate({
      ...parameter,
      omopMapping: updatedMapping
    });

    setSnackbar({
      open: true,
      message: 'Änderungsvorschlag erfolgreich eingereicht!',
      severity: 'success'
    });
  };

  // Handle manual proposal submission
  const handleSubmitManualProposal = () => {
    // Validation
    if (!manualProposalData.conceptId.trim()) {
      setSnackbar({
        open: true,
        message: 'Bitte geben Sie mindestens eine Concept ID ein!',
        severity: 'error'
      });
      return;
    }

    const newProposal = {
      id: `prop_manual_${Date.now()}`,
      concept: {
        conceptId: manualProposalData.conceptId.trim(),
        conceptCode: manualProposalData.conceptId.trim(),
        conceptName: manualProposalData.conceptName.trim() || `Concept ${manualProposalData.conceptId}`,
        vocabulary: manualProposalData.vocabulary.trim() || 'Unknown',
        domain: manualProposalData.domain.trim() || 'Measurement'
      },
      proposedBy: 'Current User',
      proposedAt: new Date().toISOString(),
      justification: manualProposalData.justification.trim() || 'Manuell vorgeschlagen',
      votes: 0,
      likes: 0,
      dislikes: 0,
      likedBy: [],
      dislikedBy: [],
      comments: [],
      status: 'pending_review'
    };

    const updatedMapping = {
      ...omopMapping,
      proposedMappings: [...proposedMappings, newProposal]
    };

    onMappingUpdate && onMappingUpdate({
      ...parameter,
      omopMapping: updatedMapping
    });

    // Reset form
    setManualProposalData({
      conceptId: '',
      conceptName: '',
      vocabulary: '',
      domain: '',
      justification: ''
    });
    setShowManualProposalForm(false);

    setSnackbar({
      open: true,
      message: 'Manueller Vorschlag erfolgreich eingereicht!',
      severity: 'success'
    });
  };

  // Handle like/dislike on proposals
  const handleLikeProposal = (proposalId) => {
    const updatedProposals = proposedMappings.map(p => {
      if (p.id === proposalId) {
        const currentUser = 'Current User';
        const alreadyLiked = p.likedBy?.includes(currentUser);
        const alreadyDisliked = p.dislikedBy?.includes(currentUser);

        if (alreadyLiked) return p; // Already liked

        return {
          ...p,
          likes: (p.likes || 0) + 1,
          likedBy: [...(p.likedBy || []), currentUser],
          // Remove dislike if exists
          dislikes: alreadyDisliked ? (p.dislikes || 0) - 1 : (p.dislikes || 0),
          dislikedBy: alreadyDisliked ? (p.dislikedBy || []).filter(u => u !== currentUser) : (p.dislikedBy || [])
        };
      }
      return p;
    });

    onMappingUpdate && onMappingUpdate({
      ...parameter,
      omopMapping: { ...omopMapping, proposedMappings: updatedProposals }
    });
  };

  const handleDislikeProposal = (proposalId) => {
    const updatedProposals = proposedMappings.map(p => {
      if (p.id === proposalId) {
        const currentUser = 'Current User';
        const alreadyLiked = p.likedBy?.includes(currentUser);
        const alreadyDisliked = p.dislikedBy?.includes(currentUser);

        if (alreadyDisliked) return p; // Already disliked

        return {
          ...p,
          dislikes: (p.dislikes || 0) + 1,
          dislikedBy: [...(p.dislikedBy || []), currentUser],
          // Remove like if exists
          likes: alreadyLiked ? (p.likes || 0) - 1 : (p.likes || 0),
          likedBy: alreadyLiked ? (p.likedBy || []).filter(u => u !== currentUser) : (p.likedBy || [])
        };
      }
      return p;
    });

    onMappingUpdate && onMappingUpdate({
      ...parameter,
      omopMapping: { ...omopMapping, proposedMappings: updatedProposals }
    });
  };

  const handleAddProposalComment = (proposalId) => {
    const commentText = proposalCommentText[proposalId];
    if (!commentText || !commentText.trim()) return;

    const updatedProposals = proposedMappings.map(p => {
      if (p.id === proposalId) {
        const newComment = {
          id: Date.now(),
          author: 'Current User',
          text: commentText.trim(),
          timestamp: new Date().toISOString()
        };
        return {
          ...p,
          comments: [...(p.comments || []), newComment]
        };
      }
      return p;
    });

    onMappingUpdate && onMappingUpdate({
      ...parameter,
      omopMapping: { ...omopMapping, proposedMappings: updatedProposals }
    });

    setProposalCommentText({ ...proposalCommentText, [proposalId]: '' });
    setShowProposalCommentInput({ ...showProposalCommentInput, [proposalId]: false });
  };

  // Handle like/dislike on mappings (current or history)
  const handleLikeMapping = (mappingId) => {
    const currentUser = 'Current User';

    // Update in history
    const updatedHistory = mappingHistory.map(m => {
      if (m.version === mappingId || (m.isCurrent && mappingId === 'current')) {
        const alreadyLiked = m.likedBy?.includes(currentUser);
        const alreadyDisliked = m.dislikedBy?.includes(currentUser);

        if (alreadyLiked) return m;

        return {
          ...m,
          likes: (m.likes || 0) + 1,
          likedBy: [...(m.likedBy || []), currentUser],
          dislikes: alreadyDisliked ? (m.dislikes || 0) - 1 : (m.dislikes || 0),
          dislikedBy: alreadyDisliked ? (m.dislikedBy || []).filter(u => u !== currentUser) : (m.dislikedBy || [])
        };
      }
      return m;
    });

    onMappingUpdate && onMappingUpdate({
      ...parameter,
      omopMapping: { ...omopMapping, mappingHistory: updatedHistory }
    });
  };

  const handleDislikeMapping = (mappingId) => {
    const currentUser = 'Current User';

    const updatedHistory = mappingHistory.map(m => {
      if (m.version === mappingId || (m.isCurrent && mappingId === 'current')) {
        const alreadyLiked = m.likedBy?.includes(currentUser);
        const alreadyDisliked = m.dislikedBy?.includes(currentUser);

        if (alreadyDisliked) return m;

        return {
          ...m,
          dislikes: (m.dislikes || 0) + 1,
          dislikedBy: [...(m.dislikedBy || []), currentUser],
          likes: alreadyLiked ? (m.likes || 0) - 1 : (m.likes || 0),
          likedBy: alreadyLiked ? (m.likedBy || []).filter(u => u !== currentUser) : (m.likedBy || [])
        };
      }
      return m;
    });

    onMappingUpdate && onMappingUpdate({
      ...parameter,
      omopMapping: { ...omopMapping, mappingHistory: updatedHistory }
    });
  };

  const handleAddMappingComment = (mappingId) => {
    const commentText = mappingCommentText[mappingId];
    if (!commentText || !commentText.trim()) return;

    const updatedHistory = mappingHistory.map(m => {
      if (m.version === mappingId || (m.isCurrent && mappingId === 'current')) {
        const newComment = {
          id: Date.now(),
          author: 'Current User',
          text: commentText.trim(),
          timestamp: new Date().toISOString()
        };
        return {
          ...m,
          comments: [...(m.comments || []), newComment]
        };
      }
      return m;
    });

    onMappingUpdate && onMappingUpdate({
      ...parameter,
      omopMapping: { ...omopMapping, mappingHistory: updatedHistory }
    });

    setMappingCommentText({ ...mappingCommentText, [mappingId]: '' });
    setShowMappingCommentInput({ ...showMappingCommentInput, [mappingId]: false });
  };

  const getStatusIcon = () => {
    switch (omopStatus) {
      case 'validated':
        return <CheckCircleIcon color="success" />;
      case 'mapped':
        return <InfoIcon color="info" />;
      case 'pending':
        return <WarningIcon color="warning" />;
      default:
        return <WarningIcon color="error" />;
    }
  };

  const getStatusLabel = () => {
    switch (omopStatus) {
      case 'validated':
        return 'Validiert';
      case 'mapped':
        return 'Gemappt';
      case 'pending':
        return 'Ausstehend';
      default:
        return 'Nicht gemappt';
    }
  };

  const getStatusColor = () => {
    switch (omopStatus) {
      case 'validated':
        return 'success';
      case 'mapped':
        return 'info';
      case 'pending':
        return 'warning';
      default:
        return 'error';
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* OMOP Mapping (Combined View) */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            {/* Header with Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              {getStatusIcon()}
              <Typography variant="h6" fontWeight="bold">
                OMOP Mapping
              </Typography>
              <Tooltip title="Was ist OMOP und warum mappen wir?">
                <IconButton
                  size="small"
                  onClick={() => setShowOmopInfoDialog(true)}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Chip
                label={getStatusLabel()}
                color={getStatusColor()}
                size="small"
                sx={{ ml: 'auto' }}
              />
            </Box>

            {/* Charite Parameter */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                Charité Parameter
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {parameter?.name || 'Unbekannter Parameter'}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {topMatch ? (
              <>
                {/* OMOP Concept Overview */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                    OMOP Concept
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                    {topMatch.conceptName}
                  </Typography>

                  {/* Matching Score Visualization */}
                  {aiMetadata && (
                    <Box sx={{ mt: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <LinearProgress
                          variant="determinate"
                          value={topMatch.matchScore || aiMetadata.overallConfidence || 85}
                          sx={{
                            flex: 1,
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              background: (topMatch.matchScore || aiMetadata.overallConfidence || 85) >= 80
                                ? 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)'
                                : (topMatch.matchScore || aiMetadata.overallConfidence || 85) >= 60
                                ? 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)'
                                : 'linear-gradient(90deg, #f44336 0%, #ef5350 100%)'
                            }
                          }}
                        />
                        <Tooltip title="KI-Konfidenz: Wie sicher ist die KI bei diesem Vorschlag?">
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            {Math.round(topMatch.matchScore || aiMetadata.overallConfidence || 85)}%
                          </Typography>
                        </Tooltip>
                      </Box>

                      {/* Matching Reasons */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {aiMetadata.matchReasons ? (
                          aiMetadata.matchReasons.map((reason, idx) => (
                            <Chip
                              key={idx}
                              label={reason}
                              size="small"
                              icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                height: 24,
                                fontSize: '0.7rem',
                                backgroundColor: '#e8f5e9',
                                color: '#2e7d32',
                                '& .MuiChip-icon': { color: '#4caf50' }
                              }}
                            />
                          ))
                        ) : (
                          <>
                            <Chip
                              label="Semantische Übereinstimmung"
                              size="small"
                              icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                height: 24,
                                fontSize: '0.7rem',
                                backgroundColor: '#e8f5e9',
                                color: '#2e7d32',
                                '& .MuiChip-icon': { color: '#4caf50' }
                              }}
                            />
                            <Chip
                              label={`${topMatch.vocabulary} Standard`}
                              size="small"
                              icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                height: 24,
                                fontSize: '0.7rem',
                                backgroundColor: '#e8f5e9',
                                color: '#2e7d32',
                                '& .MuiChip-icon': { color: '#4caf50' }
                              }}
                            />
                            {topMatch.domain && (
                              <Chip
                                label={`Domain: ${topMatch.domain}`}
                                size="small"
                                icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                                sx={{
                                  height: 24,
                                  fontSize: '0.7rem',
                                  backgroundColor: '#e8f5e9',
                                  color: '#2e7d32',
                                  '& .MuiChip-icon': { color: '#4caf50' }
                                }}
                              />
                            )}
                          </>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Concept Details with Tooltips */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={3}>
                    <Tooltip title="Eindeutige OMOP-Identifikationsnummer des Konzepts" arrow>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Concept ID
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {topMatch.conceptId}
                        </Typography>
                      </Paper>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={3}>
                    <Tooltip title="OMOP-Domäne: Kategorisiert das Konzept (z.B. Measurement für Laborwerte, Condition für Diagnosen)" arrow>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Domain
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {topMatch.domain || 'Measurement'}
                        </Typography>
                      </Paper>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={3}>
                    <Tooltip title="Ursprungsvokabular des Konzepts (z.B. LOINC für Labortests, SNOMED für klinische Begriffe)" arrow>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Vocabulary
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {topMatch.vocabulary}
                        </Typography>
                      </Paper>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={3}>
                    <Tooltip title="ATHENA öffnen für detaillierte Konzept-Informationen, Hierarchie und Synonyme" arrow>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          ATHENA
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                          component="a"
                          href={`https://athena.ohdsi.org/search-terms/terms/${topMatch.conceptId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          Link
                        </Typography>
                      </Paper>
                    </Tooltip>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* AI Generation Progress */}
                {currentStage && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {currentStage.displayName}
                    </Typography>
                    <Typography variant="caption">
                      {currentStage.description}
                    </Typography>
                    <LinearProgress sx={{ mt: 1 }} />
                  </Alert>
                )}

                {/* Action Buttons with Workflow Guidance */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                    {omopStatus === 'unmapped' && 'Starten Sie mit der KI-generierten Mapping-Empfehlung'}
                    {omopStatus === 'pending' && 'Prüfen Sie das vorgeschlagene Mapping'}
                    {omopStatus === 'mapped' && 'Validieren Sie das Mapping oder geben Sie Feedback zur Verbesserung'}
                    {omopStatus === 'validated' && 'Dieses Mapping wurde validiert'}
                    {omopStatus === 'rejected' && 'Generieren Sie ein neues Mapping oder schlagen Sie ein alternatives Konzept vor'}
                  </Typography>
                  <OmopMappingActions
                    omopStatus={omopStatus}
                    onValidate={handleValidate}
                    onReject={handleReject}
                    onEdit={() => setShowVocabSearchDialog(true)}
                    onGenerateMapping={handleGenerateMapping}
                    onOpenCritic={() => setShowCriticDialog(true)}
                    isProcessing={isProcessing}
                    hasAiMapping={!!aiMetadata}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Current Mapping with Community Features */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <CheckCircleIcon color="success" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      Aktuelles Mapping
                    </Typography>
                    {mappingHistory.length > 0 && (
                      <Chip
                        label={`${mappingHistory.length} Version${mappingHistory.length > 1 ? 'en' : ''}`}
                        size="small"
                        color="primary"
                      />
                    )}
                    <Tooltip title="Was bedeuten Likes, Dislikes und Kommentare?">
                      <IconButton
                        size="small"
                        onClick={() => setShowCommunityInfoDialog(true)}
                        sx={{ ml: 'auto' }}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

              {/* Current Mapping - Green Card */}
              {(() => {
                const currentMapping = mappingHistory.find(m => m.isCurrent) || {
                  concept: topMatch,
                  version: 'current',
                  changedBy: mappingQuality.validatedBy || 'System',
                  changedAt: mappingQuality.validatedDate || new Date().toISOString(),
                  isCurrent: true,
                  likes: communityLikes || 0,
                  dislikes: communityDislikes || 0,
                  likedBy: [],
                  dislikedBy: [],
                  comments: []
                };

                const currentUser = 'Current User';
                const hasLiked = currentMapping.likedBy?.includes(currentUser);
                const hasDisliked = currentMapping.dislikedBy?.includes(currentUser);

                return (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      mb: 1.5,
                      backgroundColor: '#e8f5e9',
                      borderColor: '#4caf50',
                      border: '2px solid'
                    }}
                  >
                    {/* Header with concept name and interaction buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <CheckCircleIcon fontSize="small" sx={{ color: '#4caf50' }} />
                        <Typography variant="body2" fontWeight="bold">
                          {currentMapping.concept?.conceptName || topMatch.conceptName}
                        </Typography>
                        <Chip
                          label={(currentMapping.concept?.vocabulary || topMatch.vocabulary)}
                          size="small"
                          sx={{ height: 16, fontSize: '0.6rem' }}
                        />
                      </Box>

                      {/* Like/Dislike/Comment buttons */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleLikeMapping('current')}
                            disabled={hasLiked}
                            sx={{ p: 0.3, color: hasLiked ? '#4caf50' : 'inherit' }}
                          >
                            <ThumbUpIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                            {currentMapping.likes || 0}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDislikeMapping('current')}
                            disabled={hasDisliked}
                            sx={{ p: 0.3, color: hasDisliked ? '#f44336' : 'inherit' }}
                          >
                            <ThumbDownIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                            {currentMapping.dislikes || 0}
                          </Typography>
                        </Box>

                        <IconButton
                          size="small"
                          onClick={() =>
                            setShowMappingCommentInput({
                              ...showMappingCommentInput,
                              ['current']: !showMappingCommentInput['current']
                            })
                          }
                          sx={{ p: 0.3 }}
                        >
                          <CommentIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                        </IconButton>

                        <IconButton
                          size="small"
                          component="a"
                          href={`https://athena.ohdsi.org/search-terms/terms/${currentMapping.concept?.conceptId || topMatch.conceptId}`}
                          target="_blank"
                          sx={{ p: 0.3 }}
                        >
                          <SearchIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Box>
                    </Box>

                    <Typography variant="caption" color="textSecondary" display="block">
                      {(currentMapping.concept?.conceptCode || topMatch.conceptCode)} • {(currentMapping.concept?.domain || topMatch.domain || 'Measurement')}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      sx={{ mb: currentMapping.comments?.length > 0 || showMappingCommentInput['current'] ? 1 : 0 }}
                    >
                      ✓ Validiert von {currentMapping.changedBy} • {new Date(currentMapping.changedAt).toLocaleDateString('de-DE')}
                      {aiMetadata && ' • 🤖 KI-generiert'}
                    </Typography>

                    {/* Comment input */}
                    {showMappingCommentInput['current'] && (
                      <Box sx={{ mt: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Kommentar hinzufügen..."
                          value={mappingCommentText['current'] || ''}
                          onChange={e =>
                            setMappingCommentText({
                              ...mappingCommentText,
                              ['current']: e.target.value
                            })
                          }
                          sx={{ mb: 0.5 }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAddMappingComment('current')}
                          disabled={!mappingCommentText['current']?.trim()}
                        >
                          Kommentieren
                        </Button>
                      </Box>
                    )}

                    {/* Comments display */}
                    {currentMapping.comments && currentMapping.comments.length > 0 && (
                      <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                        {currentMapping.comments.map((comment) => (
                          <Box key={comment.id} sx={{ mb: 0.5 }}>
                            <Typography variant="caption" fontWeight="bold">
                              {comment.author}
                            </Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                              {comment.text}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                );
              })()}

              {/* History Toggle Button */}
              {mappingHistory.filter(m => !m.isCurrent).length > 0 && (
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Button
                    size="small"
                    startIcon={showMappingHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setShowMappingHistory(!showMappingHistory)}
                    sx={{ textTransform: 'none' }}
                  >
                    {showMappingHistory ? 'Verlauf ausblenden' : `Verlauf anzeigen (${mappingHistory.filter(m => !m.isCurrent).length} ${mappingHistory.filter(m => !m.isCurrent).length === 1 ? 'Version' : 'Versionen'})`}
                  </Button>
                </Box>
              )}

              {/* History Mappings - Collapsible Gray Cards */}
              <Collapse in={showMappingHistory}>
                {mappingHistory.filter(m => !m.isCurrent).reverse().map((historyMapping) => {
                const currentUser = 'Current User';
                const hasLiked = historyMapping.likedBy?.includes(currentUser);
                const hasDisliked = historyMapping.dislikedBy?.includes(currentUser);

                return (
                  <Paper
                    key={historyMapping.version}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      mb: 1.5,
                      backgroundColor: '#f5f5f5',
                      borderColor: '#e0e0e0',
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {historyMapping.concept.conceptName}
                        </Typography>
                        <Chip
                          label={historyMapping.concept.vocabulary}
                          size="small"
                          sx={{ height: 16, fontSize: '0.6rem' }}
                        />
                        <Chip
                          label={`v${historyMapping.version}`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 16, fontSize: '0.6rem' }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleLikeMapping(historyMapping.version)}
                            disabled={hasLiked}
                            sx={{ p: 0.3, color: hasLiked ? '#4caf50' : 'inherit' }}
                          >
                            <ThumbUpIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                            {historyMapping.likes || 0}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDislikeMapping(historyMapping.version)}
                            disabled={hasDisliked}
                            sx={{ p: 0.3, color: hasDisliked ? '#f44336' : 'inherit' }}
                          >
                            <ThumbDownIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                            {historyMapping.dislikes || 0}
                          </Typography>
                        </Box>

                        <IconButton
                          size="small"
                          onClick={() =>
                            setShowMappingCommentInput({
                              ...showMappingCommentInput,
                              [historyMapping.version]: !showMappingCommentInput[historyMapping.version]
                            })
                          }
                          sx={{ p: 0.3 }}
                        >
                          <CommentIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                        </IconButton>

                        <IconButton
                          size="small"
                          component="a"
                          href={`https://athena.ohdsi.org/search-terms/terms/${historyMapping.concept.conceptId}`}
                          target="_blank"
                          sx={{ p: 0.3 }}
                        >
                          <SearchIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Box>
                    </Box>

                    <Typography variant="caption" color="textSecondary" display="block">
                      {historyMapping.concept.conceptCode} • {historyMapping.concept.domain}
                    </Typography>

                    {historyMapping.changeJustification && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        {historyMapping.changeJustification}
                      </Typography>
                    )}

                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      sx={{ mb: historyMapping.comments?.length > 0 || showMappingCommentInput[historyMapping.version] ? 1 : 0 }}
                    >
                      Geändert von {historyMapping.changedBy} • {new Date(historyMapping.changedAt).toLocaleDateString('de-DE')}
                    </Typography>

                    {showMappingCommentInput[historyMapping.version] && (
                      <Box sx={{ mt: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Kommentar hinzufügen..."
                          value={mappingCommentText[historyMapping.version] || ''}
                          onChange={e =>
                            setMappingCommentText({
                              ...mappingCommentText,
                              [historyMapping.version]: e.target.value
                            })
                          }
                          sx={{ mb: 0.5 }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAddMappingComment(historyMapping.version)}
                          disabled={!mappingCommentText[historyMapping.version]?.trim()}
                        >
                          Kommentieren
                        </Button>
                      </Box>
                    )}

                    {historyMapping.comments && historyMapping.comments.length > 0 && (
                      <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                        {historyMapping.comments.map((comment) => (
                          <Box key={comment.id} sx={{ mb: 0.5 }}>
                            <Typography variant="caption" fontWeight="bold">
                              {comment.author}
                            </Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                              {comment.text}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                );
              })}
              </Collapse>
            </Box>
              </>
            ) : (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Für diesen Parameter existiert noch kein OMOP Mapping.
                  </Typography>
                </Alert>

                {/* AI Generation Progress */}
                {currentStage && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {currentStage.displayName}
                    </Typography>
                    <Typography variant="caption">
                      {currentStage.description}
                    </Typography>
                    <LinearProgress sx={{ mt: 1 }} />
                  </Alert>
                )}

                {/* Action Buttons */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                    Starten Sie mit der KI-generierten Mapping-Empfehlung
                  </Typography>
                  <OmopMappingActions
                    omopStatus={omopStatus}
                    onValidate={handleValidate}
                    onReject={handleReject}
                    onEdit={() => setShowVocabSearchDialog(true)}
                    onGenerateMapping={handleGenerateMapping}
                    onOpenCritic={() => setShowCriticDialog(true)}
                    isProcessing={isProcessing}
                    hasAiMapping={!!aiMetadata}
                  />
                </Box>
              </>
            )}
          </Paper>
        </Grid>

        {/* Vorgeschlagene Konzepte */}
        {proposedMappings.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PendingActionsIcon color="warning" />
                <Typography variant="h6" fontWeight="bold">
                  Vorgeschlagene Konzepte
                </Typography>
                <Chip
                  label={proposedMappings.length}
                  size="small"
                  color="warning"
                />
              </Box>

              {proposedMappings.map((proposal) => {
                const currentUser = 'Current User';
                const hasLiked = proposal.likedBy?.includes(currentUser);
                const hasDisliked = proposal.dislikedBy?.includes(currentUser);

                return (
                  <Paper
                    key={proposal.id}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      mb: 1.5,
                      backgroundColor: '#fff9e6',
                      borderColor: '#ff9800',
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    {/* Header with concept name and interaction buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {proposal.concept.conceptName}
                        </Typography>
                        <Chip
                          label={proposal.concept.vocabulary}
                          size="small"
                          sx={{ height: 16, fontSize: '0.6rem' }}
                        />
                      </Box>

                      {/* Like/Dislike/Comment buttons */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleLikeProposal(proposal.id)}
                            disabled={hasLiked}
                            sx={{
                              p: 0.3,
                              color: hasLiked ? '#4caf50' : 'inherit'
                            }}
                          >
                            <ThumbUpIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                            {proposal.likes || 0}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDislikeProposal(proposal.id)}
                            disabled={hasDisliked}
                            sx={{
                              p: 0.3,
                              color: hasDisliked ? '#f44336' : 'inherit'
                            }}
                          >
                            <ThumbDownIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                            {proposal.dislikes || 0}
                          </Typography>
                        </Box>

                        {/* Comment button */}
                        <IconButton
                          size="small"
                          onClick={() =>
                            setShowProposalCommentInput({
                              ...showProposalCommentInput,
                              [proposal.id]: !showProposalCommentInput[proposal.id]
                            })
                          }
                          sx={{ p: 0.3 }}
                        >
                          <CommentIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                        </IconButton>

                        {/* ATHENA Link */}
                        <IconButton
                          size="small"
                          component="a"
                          href={`https://athena.ohdsi.org/search-terms/terms/${proposal.concept.conceptId}`}
                          target="_blank"
                          sx={{ p: 0.3 }}
                        >
                          <SearchIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Concept details */}
                    <Typography variant="caption" color="textSecondary" display="block">
                      {proposal.concept.conceptCode} • {proposal.concept.domain}
                    </Typography>

                    {/* Justification */}
                    {proposal.justification && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        {proposal.justification}
                      </Typography>
                    )}

                    {/* Proposed by */}
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      Vorgeschlagen von {proposal.proposedBy} • {new Date(proposal.proposedAt).toLocaleDateString('de-DE')}
                    </Typography>

                    {/* Accept/Reject Proposal Buttons - only for Data Stewards/Admins */}
                    {(userRole === 'data_steward' || userRole === 'admin') && (
                      <Box sx={{ display: 'flex', gap: 0.5, mb: proposal.comments?.length > 0 || showProposalCommentInput[proposal.id] ? 1 : 0 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<CheckIcon />}
                          onClick={() => {
                            console.log('Accept proposal:', proposal.id);
                            // TODO: Implement handleAcceptProposal(proposal.id)
                          }}
                          sx={{ flex: 1, py: 0.3 }}
                        >
                          Genehmigen
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CloseIcon />}
                          onClick={() => {
                            console.log('Reject proposal:', proposal.id);
                            // TODO: Implement handleRejectProposal(proposal.id)
                          }}
                          sx={{ flex: 1, py: 0.3 }}
                        >
                          Ablehnen
                        </Button>
                      </Box>
                    )}

                    {/* Comment input (collapsible) */}
                    {showProposalCommentInput[proposal.id] && (
                      <Box sx={{ mt: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Kommentar hinzufügen..."
                          value={proposalCommentText[proposal.id] || ''}
                          onChange={e =>
                            setProposalCommentText({
                              ...proposalCommentText,
                              [proposal.id]: e.target.value
                            })
                          }
                          sx={{ mb: 0.5 }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAddProposalComment(proposal.id)}
                          disabled={!proposalCommentText[proposal.id]?.trim()}
                        >
                          Kommentieren
                        </Button>
                      </Box>
                    )}

                    {/* Comments display */}
                    {proposal.comments && proposal.comments.length > 0 && (
                      <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                        {proposal.comments.map((comment) => (
                          <Box key={comment.id} sx={{ mb: 0.5 }}>
                            <Typography variant="caption" fontWeight="bold">
                              {comment.author}
                            </Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                              {comment.text}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Paper>
          </Grid>
        )}

        {/* KI-Vorschläge (Table with Pin functionality) */}
        {aiMetadata && agent3Output?.allCandidates && agent3Output.allCandidates.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LeaderboardIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  KI-Vorschläge
                </Typography>
                <Chip
                  label={`${agent3Output.allCandidates.length} Kandidaten`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>

              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Die KI hat {agent3Output.allCandidates.length} passende OMOP Concepts gefunden und nach Relevanz gerankt.
                Speichern Sie Vorschläge mit 📌, um sie auch bei neuen KI-Läufen beizubehalten.
              </Typography>

              {/* Search Bar with KI-Assistent Button */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Suche nach Concept Name, ID, Code oder Vocabulary..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => setShowFeedbackSection(!showFeedbackSection)}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #6a4193 100%)',
                    },
                    whiteSpace: 'nowrap'
                  }}
                >
                  KI-Assistent
                </Button>
              </Box>

              {/* Collapsible Feedback Section - Above Table */}
              <Collapse in={showFeedbackSection}>
                <Box sx={{
                  p: 3,
                  mb: 3,
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: 'primary.light'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <AutoAwesomeIcon color="primary" sx={{ fontSize: 28 }} />
                      <Typography variant="h6" fontWeight="bold">
                        Bessere Vorschläge generieren
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Wie funktionieren KI-Vorschläge?">
                        <IconButton size="small" onClick={() => setShowInfoDialog(true)}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={() => setShowFeedbackSection(false)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Beschreiben Sie genauer, wonach Sie suchen. Die KI analysiert Ihr Feedback und generiert
                    neue passende OMOP Concepts mit verbessertem Ranking.
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Beschreiben Sie, was Sie suchen oder warum die aktuellen Vorschläge nicht passen..."
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    disabled={isProcessing}
                    sx={{ mb: 2 }}
                    variant="outlined"
                  />
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={isProcessing ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <AutoAwesomeIcon />}
                    onClick={() => {
                      if (feedback.trim()) {
                        handleCriticFeedback(feedback);
                        setFeedback('');
                      }
                    }}
                    disabled={!feedback.trim() || isProcessing}
                    fullWidth
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5568d3 0%, #6a4193 100%)',
                      },
                      py: 1.5
                    }}
                  >
                    {isProcessing ? 'KI generiert neue Vorschläge...' : 'Neue Vorschläge generieren'}
                  </Button>
                </Box>
              </Collapse>

              <OmopSuggestionsTable
                candidates={agent3Output.allCandidates}
                currentTopMatchId={topMatch?.conceptId}
                pinnedConceptIds={pinnedConceptIds}
                onSelectCandidate={handleSelectAlternative}
                onSubmitProposal={handleSubmitProposalFromTable}
                onTogglePin={handleTogglePin}
                isProcessing={isProcessing}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
              />

              {/* Manual Proposal Form */}
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Oder manuell ein Mapping vorschlagen (z.B. über Concept ID)
                  </Typography>
                  <Button
                    size="small"
                    variant={showManualProposalForm ? "outlined" : "contained"}
                    color="primary"
                    onClick={() => setShowManualProposalForm(!showManualProposalForm)}
                  >
                    {showManualProposalForm ? 'Abbrechen' : 'Manuellen Vorschlag erstellen'}
                  </Button>
                </Box>

                <Collapse in={showManualProposalForm} timeout="auto" unmountOnExit>
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f9fafb' }}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      Neuer manueller Mapping-Vorschlag
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                      Geben Sie die Details des OMOP Concepts ein, das Sie vorschlagen möchten.
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Concept ID *"
                          placeholder="z.B. 3004249"
                          value={manualProposalData.conceptId}
                          onChange={(e) => setManualProposalData({ ...manualProposalData, conceptId: e.target.value })}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Concept Name"
                          placeholder="z.B. Systolic blood pressure"
                          value={manualProposalData.conceptName}
                          onChange={(e) => setManualProposalData({ ...manualProposalData, conceptName: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Vocabulary"
                          placeholder="z.B. LOINC, SNOMED"
                          value={manualProposalData.vocabulary}
                          onChange={(e) => setManualProposalData({ ...manualProposalData, vocabulary: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Domain"
                          placeholder="z.B. Measurement, Observation"
                          value={manualProposalData.domain}
                          onChange={(e) => setManualProposalData({ ...manualProposalData, domain: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          rows={3}
                          label="Begründung"
                          placeholder="Warum schlagen Sie dieses Concept vor?"
                          value={manualProposalData.justification}
                          onChange={(e) => setManualProposalData({ ...manualProposalData, justification: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setShowManualProposalForm(false);
                              setManualProposalData({
                                conceptId: '',
                                conceptName: '',
                                vocabulary: '',
                                domain: '',
                                justification: ''
                              });
                            }}
                          >
                            Abbrechen
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleSubmitManualProposal}
                            disabled={!manualProposalData.conceptId.trim()}
                          >
                            Vorschlag einreichen
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Collapse>
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Previous Feedback History - Only show if there's history */}
        {criticHistory.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HistoryIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight="bold">
                    Bisherige Verbesserungen
                  </Typography>
                  <Chip label={criticHistory.length} size="small" color="primary" variant="outlined" />
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowCriticDialog(true)}
                >
                  Details anzeigen
                </Button>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Sie haben bereits {criticHistory.length} {criticHistory.length === 1 ? 'Verbesserung' : 'Verbesserungen'} durchgeführt.
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Feedback Dialog */}
      <Dialog
        open={showCriticDialog}
        onClose={() => setShowCriticDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Vorschlag verbessern</DialogTitle>
        <DialogContent>
          <OmopCriticAgentPanel
            criticHistory={criticHistory}
            onSubmitFeedback={handleCriticFeedback}
            isProcessing={isProcessing}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCriticDialog(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Vocabulary Search Dialog */}
      <Dialog
        open={showVocabSearchDialog}
        onClose={() => setShowVocabSearchDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>OMOP Vocabulary Suche</DialogTitle>
        <DialogContent>
          <OmopVocabularySearch
            onSelectConcept={handleManualMapping}
            parameterName={parameter?.name}
            isSearching={isProcessing}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowVocabSearchDialog(false)}>Abbrechen</Button>
        </DialogActions>
      </Dialog>

      {/* Info Dialog - Wie funktionieren KI-Vorschläge? */}
      <Dialog
        open={showInfoDialog}
        onClose={() => setShowInfoDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PsychologyIcon color="primary" />
            <Typography variant="h6">Wie funktionieren KI-Vorschläge?</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" paragraph>
              Das Multi-Agent-System durchläuft mehrere Schritte, um passende OMOP Concepts zu finden:
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                🔍 Schritt 1: Semantische Analyse
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Die KI analysiert den Parameternamen, Tags, Kategorien und Kontext (z.B. ICU, Labor).
                Sie erstellt eine strukturierte Beschreibung des klinischen Konzepts.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                🔎 Schritt 2: OMOP Vocabulary Suche
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Basierend auf der Analyse werden gezielte Suchanfragen in der lokalen OMOP Vocabulary
                Datenbank ausgeführt (LOINC, SNOMED, RxNorm, etc.). Die KI findet 8-15 potenzielle Kandidaten.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                📊 Schritt 3: Ranking & Scoring
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Alle gefundenen Concepts werden bewertet und nach Relevanz gerankt. Das Scoring berücksichtigt:
                Namensübereinstimmung, Vocabulary-Standards, Domain-Passung und klinischen Kontext.
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              💡 Tipps zur Nutzung:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 1 }}>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>Hohe Scores (&gt;90%):</strong> Meist korrekt, können direkt validiert werden
                </Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>Mittlere Scores (70-90%):</strong> Sollten manuell geprüft werden
                </Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>Niedrige Scores (&lt;70%):</strong> Nutzen Sie den KI-Assistent für verbesserte Vorschläge
                </Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  <strong>📌 Pin-Funktion:</strong> Speichern Sie gute Vorschläge, um sie bei neuen
                  KI-Läufen beizubehalten
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              ✨ Bessere Vorschläge mit dem KI-Assistent:
            </Typography>
            <Typography variant="body2" paragraph>
              Der KI-Assistent verfeinert die Suche basierend auf Ihrem Feedback. Je präziser Sie
              beschreiben, was Sie suchen, desto besser werden die neuen Vorschläge.
            </Typography>
            <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
              Beispiele für hilfreiches Feedback:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 0 }}>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  "Ich suche speziell systolischen Blutdruck in <strong>sitzender Position</strong>"
                </Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  "Das Konzept sollte aus <strong>SNOMED</strong> statt LOINC sein"
                </Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  "Zu allgemein - ich brauche ein Konzept für <strong>pädiatrische Patienten</strong>"
                </Typography>
              </Box>
              <Box component="li" sx={{ mb: 0 }}>
                <Typography variant="body2">
                  "Die Domain sollte <strong>Observation</strong> statt Measurement sein"
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInfoDialog(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Comments Dialog */}
      <Dialog
        open={showCommentsDialog}
        onClose={() => setShowCommentsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CommentIcon color="primary" />
            <Typography variant="h6">Diskussion ({comments.length})</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {comments.length > 0 ? (
            <Box>
              {comments.map(comment => (
                <Paper key={comment.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {comment.author}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {comment.authorRole} • {new Date(comment.timestamp).toLocaleDateString('de-DE')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ThumbUpOutlinedIcon fontSize="small" />
                      <Typography variant="body2">{comment.likes}</Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2">{comment.text}</Typography>
                  {comment.replies.length > 0 && (
                    <Box sx={{ ml: 4, mt: 2 }}>
                      {comment.replies.map(reply => (
                        <Paper key={reply.id} variant="outlined" sx={{ p: 1.5, mb: 1, backgroundColor: '#f5f5f5' }}>
                          <Typography variant="caption" fontWeight="bold" display="block">
                            {reply.author}
                          </Typography>
                          <Typography variant="body2">{reply.text}</Typography>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Paper>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Noch keine Kommentare vorhanden.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCommentsDialog(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog
        open={showHistoryDialog}
        onClose={() => setShowHistoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            <Typography variant="h6">Mapping-Verlauf</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {mappingHistory.length > 0 ? (
            <Box>
              {mappingHistory.map((entry, idx) => (
                <Paper
                  key={idx}
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 2,
                    borderLeft: entry.isCurrent ? '4px solid #4caf50' : '4px solid #e0e0e0'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        v{entry.version} {entry.isCurrent && '✅ Aktuell'}
                      </Typography>
                      <Typography variant="body2" gutterBottom>
                        {entry.concept.conceptName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {entry.concept.vocabulary} {entry.concept.conceptCode}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(entry.changedAt).toLocaleDateString('de-DE')}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" display="block" gutterBottom>
                    <strong>Geändert von:</strong> {entry.changedBy}
                  </Typography>
                  <Typography variant="caption" display="block">
                    <strong>Grund:</strong> {entry.changeJustification}
                  </Typography>
                </Paper>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              Kein Verlauf verfügbar.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHistoryDialog(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* OMOP Info Dialog */}
      <Dialog
        open={showOmopInfoDialog}
        onClose={() => setShowOmopInfoDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" />
          Was ist OMOP Mapping?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            <strong>OMOP (Observational Medical Outcomes Partnership)</strong> ist ein internationaler Standard
            für die Harmonisierung klinischer Daten aus verschiedenen Quellen.
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Warum mappen wir Charité-Parameter zu OMOP?
          </Typography>
          <Typography variant="body2" component="div">
            • <strong>Interoperabilität:</strong> Ermöglicht den Austausch von Daten mit anderen Kliniken und Forschungseinrichtungen<br/>
            • <strong>Standardisierung:</strong> Einheitliche Begriffe statt klinikeigener Terminologie<br/>
            • <strong>Forschung:</strong> Vereinfacht Multi-Center-Studien und Big-Data-Analysen<br/>
            • <strong>Qualität:</strong> Reduziert Fehler durch eindeutige Zuordnungen<br/>
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Was ist ein OMOP Concept?
          </Typography>
          <Typography variant="body2">
            Ein OMOP Concept ist eine eindeutige Repräsentation eines medizinischen Begriffs (z.B. ein Laborwert,
            eine Diagnose, ein Medikament). Jedes Concept hat eine eindeutige ID und gehört zu einem
            standardisierten Vokabular (z.B. LOINC für Laborwerte, SNOMED für klinische Begriffe).
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Ihre Rolle beim Mapping
          </Typography>
          <Typography variant="body2">
            Als medizinischer Experte validieren Sie KI-generierte Mappings oder schlagen bessere Alternativen vor.
            Ihre Expertise ist entscheidend für die Qualität und Genauigkeit der Datenstandardisierung.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOmopInfoDialog(false)} variant="contained">
            Verstanden
          </Button>
        </DialogActions>
      </Dialog>

      {/* Community Features Info Dialog */}
      <Dialog
        open={showCommunityInfoDialog}
        onClose={() => setShowCommunityInfoDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" />
          Community-Bewertung
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Das OMOP Mapping ist ein kollaborativer Prozess. Ihre Bewertungen helfen dem gesamten Team:
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mb: 2 }}>
              <ThumbUpIcon color="success" sx={{ mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  Likes (Zustimmung)
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Zeigen Sie, dass ein Mapping korrekt und gut gewählt ist. Mappings mit vielen Likes
                  werden für andere Nutzer priorisiert angezeigt.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mb: 2 }}>
              <ThumbDownIcon color="error" sx={{ mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  Dislikes (Ablehnung)
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Markieren Sie problematische oder falsche Mappings. Dies signalisiert anderen Nutzern,
                  dass eine Überprüfung notwendig ist.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
              <CommentIcon color="primary" sx={{ mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  Kommentare (Diskussion)
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Teilen Sie Ihre medizinische Expertise: Warum ist ein Mapping gut oder problematisch?
                  Welche Alternative wäre besser? Ihre Kommentare helfen Kollegen bei der Entscheidung.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Tipp:</strong> Je mehr das Team sich beteiligt, desto schneller entstehen
              hochqualitative, validierte Mappings für alle Parameter.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCommunityInfoDialog(false)} variant="contained">
            Verstanden
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default OmopMappingDetail;
